import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader2, Sparkles, ImagePlus, X } from 'lucide-react';
import { createEvent } from '../lib/events';
import { useAuth } from '../lib/auth';
import PhotoUploader from '../components/PhotoUploader';

export default function HostEvent() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [f, setF] = React.useState({
    title: '', category: 'Meet', date: '', time: '', city: '', venue: '', desc: '',
  });
  const [imgs, setImgs] = React.useState([]);
  const [submitting, setSubmitting] = React.useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Sign in to host an event');
      navigate('/signin');
      return;
    }
    if (!f.title.trim()) return toast.error('Title is required');
    if (!f.date) return toast.error('Date is required');
    try {
      setSubmitting(true);
      const created = await createEvent({
        title: f.title.trim(),
        category: f.category,
        date: f.date,
        time: f.time || null,
        city: f.city.trim() || null,
        venue: f.venue.trim() || null,
        description: f.desc.trim(),
        img: imgs[0] || null,
      });
      toast.success('Event published');
      navigate(`/events?id=${created.id}`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || err.message || 'Publish failed');
    } finally {
      setSubmitting(false);
    }
  };

  const input = 'w-full bg-[#0d0d0d] hairline px-3 py-2.5 text-sm placeholder:text-zinc-600 focus:outline-none focus:border-[#ff2a2a]';
  const label = 'text-xs font-mono uppercase tracking-widest text-zinc-500 mb-1 block';

  return (
    <div className="mx-auto max-w-4xl px-6 lg:px-10 py-14" data-testid="page-host-event">
      <div className="font-mono text-xs uppercase tracking-widest text-[#ff2a2a] mb-2">Host an event</div>
      <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight leading-none">Bring the culture together.</h1>
      <p className="mt-3 text-zinc-400 text-sm">Meets, cars &amp; coffee, track days, shows. It&rsquo;s free to host.</p>

      {!user && (
        <div className="mt-6 hairline p-4 bg-[#141414] text-sm text-zinc-300" data-testid="host-signin-note">
          You need to be signed in to host an event.{' '}
          <button onClick={() => navigate('/signin')} className="text-[#ff2a2a] underline hover:no-underline">Sign in</button> first.
        </div>
      )}

      <form onSubmit={submit} className="mt-10 am-card p-6 grid md:grid-cols-2 gap-4" data-testid="host-form">
        <div className="md:col-span-2">
          <label className={label}>Title *</label>
          <input data-testid="host-title" className={input} value={f.title} onChange={set('title')} placeholder="Downtown Night Meet" />
        </div>
        <div>
          <label className={label}>Category</label>
          <select data-testid="host-category" className={input} value={f.category} onChange={set('category')}>
            {['Meet', 'Cars & Coffee', 'Track Day', 'Show'].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className={label}>Date *</label>
          <input data-testid="host-date" type="date" className={input} value={f.date} onChange={set('date')} />
        </div>
        <div>
          <label className={label}>Time</label>
          <input data-testid="host-time" type="time" className={input} value={f.time} onChange={set('time')} />
        </div>
        <div>
          <label className={label}>City</label>
          <input data-testid="host-city" className={input} value={f.city} onChange={set('city')} placeholder="Toronto, ON" />
        </div>
        <div className="md:col-span-2">
          <label className={label}>Venue</label>
          <input data-testid="host-venue" className={input} value={f.venue} onChange={set('venue')} placeholder="Cherry Beach Lot" />
        </div>
        <div className="md:col-span-2">
          <label className={label}>Event image (optional)</label>
          <PhotoUploader value={imgs} onChange={(v) => setImgs(v.slice(0, 1))} />
        </div>
        <div className="md:col-span-2">
          <label className={label}>Description</label>
          <textarea data-testid="host-desc" rows={5} className={input} value={f.desc} onChange={set('desc')} placeholder="What's the vibe? Who should come?" />
        </div>
        <div className="md:col-span-2 flex items-center justify-between pt-2">
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 inline-flex items-center gap-1.5">
            <Sparkles className="w-3.5 h-3.5 text-emerald-400" /> Free to host · Public event
          </div>
          <button
            data-testid="host-submit"
            disabled={submitting || !user}
            type="submit"
            className="bg-[#ff2a2a] hover:bg-[#e01f1f] disabled:opacity-60 text-white text-sm font-semibold px-6 py-3 transition-colors inline-flex items-center gap-2"
          >
            {submitting && <Loader2 className="w-4 h-4 animate-spin" />}
            {submitting ? 'Publishing…' : 'Publish event'}
          </button>
        </div>
      </form>
    </div>
  );
}
